import {
  NONCONFORMITY_SOURCE_LABEL_PT_BR,
  NONCONFORMITY_TYPE_LABEL_PT_BR,
  NonconformitySource,
  NonconformityType,
  type SgqIndicatorsQuery,
} from '@qualicloud/contracts';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import { BarChart, TrendChart, type ChartDatum } from '@/components/sgq/accessible-chart';
import {
  Button,
  Card,
  CardHeader,
  DatePicker,
  EmptyState,
  Field,
  IconAlertTriangle,
  IconCheckCircle,
  IconDownload,
  IconInfo,
  IconPrinter,
  LoadingRegion,
  MetricCard,
  Select,
  SkeletonCard,
  parseIso,
  useToast,
} from '@/design-system';
import { queryKeys, saveBlob, sgqIndicatorsApi, toUserMessage } from '@/lib/api';
import type { SgqIndicators } from '@/lib/api/sgq.types';
import { formatRegulatedDateTime } from '@/lib/format/datetime';
import { useDirectory } from '@/lib/use-directory';
import { useOrganizationTimeZone } from '@/providers/auth-provider';
import type { SgqIndicatorsSearch } from '@/routes/search-schemas';
import { PageBody, PageHeader } from '@/shell/app-shell';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * INDICADORES DO SGQ — sgq.md §E
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * É O INSUMO DA ANÁLISE CRÍTICA PELA DIREÇÃO (ISO 13485 §5.6), que exige
 * retroalimentação, reclamação, auditoria, monitoramento de processo e ação
 * corretiva/preventiva como entradas.
 *
 * §E.6 é explícito: NENHUM destes indicadores existia no legado — nem tempo
 * médio de tratamento, nem taxa de reincidência, nem aderência de prazo, nem
 * eficácia acumulada, nem distribuição por categoria. O painel que existia
 * ZERAVA dois dos quatro baldes de NC (`atrasadas` e `em_espera` literais,
 * BUG-IND-03) e, no de tarefas, somava baldes que se sobrepunham — o total
 * ficava maior que o número real de tarefas (BUG-IND-01).
 *
 * Aqui os baldes são mutuamente exclusivos e a soma fecha com o total. Cada
 * gráfico tem TABELA ALTERNATIVA sempre presente no DOM: a análise crítica é
 * impressa, e um `<canvas>` não imprime nem é lido por leitor de tela.
 */
export function SgqIndicatorsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const timeZone = useOrganizationTimeZone();
  const directory = useDirectory();
  const search = useSearch({ strict: false }) as unknown as SgqIndicatorsSearch;

  const query: Partial<SgqIndicatorsQuery> = useMemo(
    () => ({
      // A URL guarda `AAAA-MM-DD`; a consulta do contrato pede `Date`.
      from: parseIso(search.from ?? '') ?? undefined,
      to: parseIso(search.to ?? '') ?? undefined,
      departmentId: search.departmentId,
      source: search.source as NonconformitySource[] | undefined,
      type: search.type as NonconformityType[] | undefined,
      bucket: (search.bucket as 'MONTH' | 'QUARTER') ?? 'MONTH',
      recurrenceWindowDays: search.recurrenceWindowDays ?? 365,
    }),
    [search],
  );

  const update = useCallback(
    (patch: Partial<SgqIndicatorsSearch>) => {
      void navigate({
        to: '/indicadores-sgq',
        search: { ...search, ...patch } as SgqIndicatorsSearch,
      });
    },
    [navigate, search],
  );

  const indicatorsQuery = useQuery({
    queryKey: queryKeys.sgqIndicators.query(query),
    queryFn: () => sgqIndicatorsApi.query(query),
    placeholderData: keepPreviousData,
  });

  const data = indicatorsQuery.data;

  /**
   * EXPORTAÇÃO — agora do SERVIDOR.
   *
   * Este bloco montava o CSV no CLIENTE, com a justificativa (correta na época)
   * de que não havia rota publicada no gateway e oferecer um botão que sempre
   * falha seria pior do que não oferecer. A rota existe:
   * `GET /sgq/indicators/export`.
   *
   * Por que a mudança importa: um `Blob` construído no navegador não deixa
   * rastro nenhum. A folha que vai à análise crítica pela direção precisa de um
   * evento `EXPORT` na trilha dizendo quem a emitiu, com qual recorte e qual
   * artefato saiu — o mesmo tratamento que a Lista Mestra do GED recebe. E o
   * PDF, que o cliente não tinha como produzir, passa a existir.
   */
  const exportReport = useMutation({
    mutationFn: async (format: 'csv' | 'pdf') => {
      const file = await sgqIndicatorsApi.exportReport(query, format);
      saveBlob(file.blob, file.fileName);
    },
    onSuccess: () =>
      toast({
        tone: 'success',
        title: 'Indicadores exportados',
        description:
          'O arquivo saiu do servidor com o mesmo recorte da tela, e a exportação ficou registrada na trilha de auditoria.',
      }),
    onError: (error) =>
      toast({
        tone: 'error',
        title: 'Não foi possível exportar',
        description: toUserMessage(error),
      }),
  });

  return (
    <>
      <PageHeader
        title="Indicadores do SGQ"
        description="Entradas da análise crítica pela direção — ISO 13485 §5.6."
        actions={
          <>
            <Button
              variant="secondary"
              leadingIcon={<IconDownload size={15} />}
              disabled={!data}
              loading={exportReport.isPending && exportReport.variables === 'csv'}
              onClick={() => exportReport.mutate('csv')}
            >
              Exportar CSV
            </Button>
            <Button
              variant="secondary"
              leadingIcon={<IconDownload size={15} />}
              disabled={!data}
              loading={exportReport.isPending && exportReport.variables === 'pdf'}
              onClick={() => exportReport.mutate('pdf')}
            >
              Exportar PDF
            </Button>
            <Button
              variant="secondary"
              leadingIcon={<IconPrinter size={15} />}
              onClick={() => window.print()}
            >
              Imprimir
            </Button>
          </>
        }
      />

      <PageBody className="flex flex-col gap-6">
        <Card padding="sm">
          <section aria-label="Período e recortes dos indicadores" className="flex flex-wrap items-end gap-3">
            <div className="w-44">
              <Field label="De">
                <DatePicker
                  value={search.from ?? ''}
                  onValueChange={(iso) => update({ from: iso || undefined })}
                />
              </Field>
            </div>
            <div className="w-44">
              <Field label="Até">
                <DatePicker
                  value={search.to ?? ''}
                  onValueChange={(iso) => update({ to: iso || undefined })}
                />
              </Field>
            </div>
            <div className="w-44">
              <Field label="Granularidade">
                <Select
                  value={search.bucket ?? 'MONTH'}
                  onValueChange={(value) => update({ bucket: value })}
                  options={[
                    { value: 'MONTH', label: 'Mensal' },
                    { value: 'QUARTER', label: 'Trimestral' },
                  ]}
                />
              </Field>
            </div>
            <div className="w-56">
              <Field label="Departamento">
                <Select
                  value={search.departmentId ?? '__all__'}
                  onValueChange={(value) =>
                    update({ departmentId: value === '__all__' ? undefined : value })
                  }
                  options={[
                    { value: '__all__', label: 'Todos os departamentos' },
                    ...directory.departments.map((department) => ({
                      value: department.id,
                      label: department.name,
                    })),
                  ]}
                />
              </Field>
            </div>
            <div className="w-56">
              <Field label="Origem">
                <Select
                  value={search.source?.[0] ?? '__all__'}
                  onValueChange={(value) =>
                    update({ source: value === '__all__' ? undefined : [value] })
                  }
                  options={[
                    { value: '__all__', label: 'Todas as origens' },
                    ...Object.values(NonconformitySource).map((source) => ({
                      value: source,
                      label: NONCONFORMITY_SOURCE_LABEL_PT_BR[source],
                    })),
                  ]}
                />
              </Field>
            </div>
            <div className="w-56">
              <Field label="Natureza">
                <Select
                  value={search.type?.[0] ?? '__all__'}
                  onValueChange={(value) =>
                    update({ type: value === '__all__' ? undefined : [value] })
                  }
                  options={[
                    { value: '__all__', label: 'Todas as naturezas' },
                    ...Object.values(NonconformityType).map((type) => ({
                      value: type,
                      label: NONCONFORMITY_TYPE_LABEL_PT_BR[type],
                    })),
                  ]}
                />
              </Field>
            </div>
          </section>
        </Card>

        <LoadingRegion loading={indicatorsQuery.isPending} label="Calculando os indicadores…">
          {indicatorsQuery.isPending ? (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {Array.from({ length: 8 }, (_, index) => (
                <SkeletonCard key={index} />
              ))}
            </div>
          ) : !data ? (
            <EmptyState
              icon={<IconAlertTriangle size={22} />}
              title="Indicadores indisponíveis"
              description="Não foi possível calcular os indicadores agora. Recarregue a página em alguns instantes; se o problema persistir, informe o gestor da qualidade."
              action={
                <Button variant="secondary" onClick={() => void indicatorsQuery.refetch()}>
                  Tentar novamente
                </Button>
              }
            />
          ) : (
            <div className="flex flex-col gap-6">
              <p className="text-sm text-content-muted">
                Período de {formatIsoAsBr(data.period.from)} a {formatIsoAsBr(data.period.to)} ·
                cálculo do servidor em {formatRegulatedDateTime(data.generatedAt, timeZone)}.
              </p>

              {/* ── Cartões ───────────────────────────────────────────── */}
              <section aria-labelledby="sgq-headline-heading">
                <h2 id="sgq-headline-heading" className="mb-3 text-base font-semibold text-content">
                  Visão geral do período
                </h2>
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  <MetricCard
                    label="Não-conformidades"
                    value={data.nonconformity.total}
                    hint="Registros abertos no período. A soma dos baldes abaixo fecha com este total."
                  />
                  <GoalMetricCard
                    label="Taxa de reincidência"
                    value={`${format1(data.recurrence.ratePercent)}%`}
                    met={data.recurrence.ratePercent <= 20}
                    metTone="neutral"
                    goal="até 20%"
                    offGoalVerdict="Acima da meta."
                    hint={`${data.recurrence.recurrentCount} de ${data.recurrence.total} registros declararam origem anterior, em janela de ${data.recurrence.windowDays} dias.`}
                  />
                  <MetricCard
                    label="Tempo médio de tratamento"
                    value={`${format1(data.treatmentTime.averageDays)} dias`}
                    hint={`Mediana ${format1(data.treatmentTime.medianDays)} dias, máximo ${format1(data.treatmentTime.maxDays)}. Só registros encerrados entram no cálculo (${data.treatmentTime.closedCount}).`}
                  />
                  <GoalMetricCard
                    label="Aderência de prazo das ações"
                    value={`${format1(data.capaAdherence.onTimeRatePercent)}%`}
                    met={data.capaAdherence.onTimeRatePercent >= 80}
                    goal="80% no prazo"
                    offGoalVerdict="Abaixo da meta."
                    hint={`${data.capaAdherence.buckets.onTimeCompleted} de ${data.capaAdherence.total} ações concluídas dentro do prazo.`}
                  />
                </div>
              </section>

              {/* ── NC por dimensão ───────────────────────────────────── */}
              <section aria-labelledby="sgq-nc-heading" className="flex flex-col gap-4">
                <h2 id="sgq-nc-heading" className="text-base font-semibold text-content">
                  Não-conformidades
                </h2>

                <div className="grid gap-4 xl:grid-cols-2">
                  <BarChart
                    title="Situação atual"
                    description="Cada registro cai em exatamente um balde."
                    categoryHeader="Situação"
                    data={statusData(data)}
                  />
                  <BarChart
                    title="Por natureza"
                    description="Não-conformidade, observação, oportunidade de melhoria, evento adverso e reclamação têm severidades regulatórias diferentes."
                    categoryHeader="Natureza"
                    data={toChartData(data.nonconformity.byType)}
                  />
                  <BarChart
                    title="Por origem"
                    description="Distribuição das não-conformidades pela origem informada na abertura."
                    categoryHeader="Origem"
                    data={toChartData(data.nonconformity.bySource)}
                  />
                  <BarChart
                    title="Por departamento"
                    description="Setor onde a não-conformidade foi detectada."
                    categoryHeader="Departamento"
                    data={toChartData(data.nonconformity.byDepartment)}
                  />
                  <BarChart
                    title="Por faixa de criticidade GUT"
                    description="Faixas calculadas com os limiares configurados nesta organização."
                    categoryHeader="Faixa"
                    data={criticalityData(data)}
                  />
                  <TrendChart
                    title="Tendência: abertas × encerradas"
                    description="Saldo positivo por vários períodos significa que a organização abre mais não-conformidades do que encerra."
                    data={[...data.nonconformity.trend]}
                  />
                </div>
              </section>

              {/* ── CAPA e eficácia ───────────────────────────────────── */}
              <section aria-labelledby="sgq-capa-heading" className="flex flex-col gap-4">
                <h2 id="sgq-capa-heading" className="text-base font-semibold text-content">
                  Ações corretivas e eficácia
                </h2>

                <div className="grid gap-4 xl:grid-cols-2">
                  <BarChart
                    title="Aderência de prazo das ações"
                    description="Baldes mutuamente exclusivos: a soma fecha com o total."
                    categoryHeader="Situação de prazo"
                    data={adherenceData(data)}
                  />
                  <BarChart
                    title="Verificação de eficácia"
                    description="Aprovada, reprovada e pendente. Cada reprovação gera automaticamente uma SACAP de follow-up em D+3."
                    categoryHeader="Resultado"
                    data={effectivenessData(data)}
                  />
                </div>

                <Card>
                  <CardHeader
                    headingLevel={3}
                    title="Eficácia acumulada"
                    description="Percentual de verificações aprovadas sobre o total de verificações concluídas no período."
                  />
                  <div className="mt-3 flex flex-wrap items-center gap-4">
                    <p className="text-3xl font-semibold text-content qc-tabular">
                      {format1(data.effectiveness.approvalRatePercent)}%
                    </p>
                    <p className="text-sm text-content-muted">
                      {data.effectiveness.approved} aprovadas ·{' '}
                      {data.effectiveness.rejected} reprovadas ·{' '}
                      {data.effectiveness.pending} aguardando verificação
                    </p>
                  </div>
                </Card>
              </section>

              {/* ── Circulares ────────────────────────────────────────── */}
              <section aria-labelledby="sgq-circular-heading" className="flex flex-col gap-4">
                <h2 id="sgq-circular-heading" className="text-base font-semibold text-content">
                  Cobertura de circular
                </h2>

                <Card>
                  <CardHeader
                    headingLevel={3}
                    title="Leitura das circulares publicadas"
                    description="É a evidência de comunicação interna."
                  />
                  <div className="mt-3 grid gap-3 sm:grid-cols-4">
                    <MetricCard
                      label="Circulares"
                      value={data.circularCoverage.circularCount}
                      hint="Publicadas no período."
                    />
                    <MetricCard
                      label="Destinatários"
                      value={data.circularCoverage.recipientCount}
                      hint="Somando todas as circulares do período."
                    />
                    <MetricCard
                      label="Com ciência"
                      value={data.circularCoverage.acknowledgedCount}
                      tone="success"
                      hint="Ciências assinadas registradas."
                    />
                    <GoalMetricCard
                      label="Cobertura"
                      value={`${format1(data.circularCoverage.coveragePercent)}%`}
                      met={data.circularCoverage.coveragePercent >= 90}
                      goal="90% dos destinatários"
                      offGoalVerdict="Abaixo da meta."
                      hint="Percentual de destinatários que registraram ciência."
                    />
                  </div>
                </Card>

                <BarChart
                  title="Cobertura por circular"
                  description="Circulares com cobertura baixa são as que exigem cobrança."
                  categoryHeader="Circular"
                  valueHeader="Ciências"
                  data={data.circularCoverage.items.map((item) => ({
                    key: item.circularId,
                    label: `${item.code} — ${item.title}`,
                    value: item.acknowledgedCount,
                    percentage: item.coveragePercent,
                    emphasis:
                      item.coveragePercent >= 100
                        ? 'success'
                        : item.coveragePercent >= 50
                          ? 'warning'
                          : 'danger',
                  }))}
                  emptyTitle="Nenhuma circular publicada no período"
                  emptyDescription="Sem circular publicada não há cobertura de leitura a apurar."
                />
              </section>

              <p className="flex items-start gap-2 rounded-md border border-info bg-info-subtle px-3 py-2.5 text-sm text-info-content">
                <IconInfo size={15} aria-hidden="true" className="mt-0.5 shrink-0" />
                <span>
                  Todo gráfico desta tela tem uma tabela de dados equivalente, sempre presente para
                  leitores de tela e alternável pelo botão “Ver como tabela”. A exportação em CSV
                  reproduz exatamente estes números.
                </span>
              </p>
            </div>
          )}
        </LoadingRegion>
      </PageBody>
    </>
  );
}

/**
 * Cartão de métrica que carrega um julgamento de meta.
 *
 * O `tone` do MetricCard se traduz numa borda colorida à esquerda, e só nisso.
 * Esta folha é impressa para a análise crítica pela direção: no papel, e para
 * quem não distingue as cores, a borda não diz qual é o limiar nem que ele foi
 * ultrapassado. Por isso a meta e o veredito vão no `hint`, em texto, e o ícone
 * repete o mesmo fato — a cor fica como reforço.
 *
 * A decisão vem de UM booleano (`met`), que governa borda, ícone e frase ao
 * mesmo tempo: se cada um recalculasse o limiar por conta própria, o cartão
 * poderia mostrar borda de alerta com a frase "dentro da meta".
 */
function GoalMetricCard({
  label,
  value,
  met,
  goal,
  offGoalVerdict,
  hint,
  metTone = 'success',
}: {
  readonly label: string;
  readonly value: string;
  readonly met: boolean;
  /** A meta como o usuário a lê, ex.: "até 20%". */
  readonly goal: string;
  /** Veredito quando a meta não foi atingida, ex.: "Acima da meta." */
  readonly offGoalVerdict: string;
  readonly hint?: string;
  /** Nem toda meta atingida merece destaque: reincidência baixa é o normal. */
  readonly metTone?: 'neutral' | 'success';
}) {
  return (
    <MetricCard
      label={label}
      value={value}
      tone={met ? metTone : 'warning'}
      icon={met ? <IconCheckCircle size={16} /> : <IconAlertTriangle size={16} />}
      hint={`${hint ? `${hint} ` : ''}Meta: ${goal}. ${met ? 'Dentro da meta.' : offGoalVerdict}`}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Preparação dos dados
// ─────────────────────────────────────────────────────────────────────────────

function toChartData(slices: SgqIndicators['nonconformity']['byType']): ChartDatum[] {
  return slices.map((slice) => ({
    key: slice.key,
    label: slice.label,
    value: slice.count,
    percentage: slice.percentage,
  }));
}

function statusData(data: SgqIndicators): ChartDatum[] {
  const buckets = data.nonconformity.buckets;
  return [
    { key: 'open', label: 'Aberta', value: buckets.open, emphasis: 'warning' },
    {
      key: 'underEvaluation',
      label: 'Em avaliação',
      value: buckets.underEvaluation,
      emphasis: 'warning',
    },
    { key: 'inTreatment', label: 'Em tratamento', value: buckets.inTreatment },
    {
      key: 'pendingEffectiveness',
      label: 'Aguardando eficácia',
      value: buckets.pendingEffectiveness,
      emphasis: 'warning',
    },
    { key: 'closed', label: 'Encerrada', value: buckets.closed, emphasis: 'success' },
    { key: 'notApplicable', label: 'Improcedente', value: buckets.notApplicable },
  ];
}

function criticalityData(data: SgqIndicators): ChartDatum[] {
  const EMPHASIS: Record<string, ChartDatum['emphasis']> = {
    HIGH: 'danger',
    MEDIUM: 'warning',
    LOW: 'success',
  };
  return data.nonconformity.byCriticality.map((slice) => ({
    key: slice.key,
    label: slice.label,
    value: slice.count,
    percentage: slice.percentage,
    emphasis: EMPHASIS[slice.key] ?? 'neutral',
  }));
}

function adherenceData(data: SgqIndicators): ChartDatum[] {
  const buckets = data.capaAdherence.buckets;
  return [
    {
      key: 'onTimeCompleted',
      label: 'Concluída no prazo',
      value: buckets.onTimeCompleted,
      emphasis: 'success',
    },
    {
      key: 'lateCompleted',
      label: 'Concluída com atraso',
      value: buckets.lateCompleted,
      emphasis: 'warning',
    },
    { key: 'openOnTime', label: 'Em aberto, no prazo', value: buckets.openOnTime },
    {
      key: 'openOverdue',
      label: 'Em aberto, vencida',
      value: buckets.openOverdue,
      emphasis: 'danger',
    },
    { key: 'cancelled', label: 'Cancelada', value: buckets.cancelled },
  ];
}

function effectivenessData(data: SgqIndicators): ChartDatum[] {
  return [
    { key: 'approved', label: 'Aprovada', value: data.effectiveness.approved, emphasis: 'success' },
    { key: 'rejected', label: 'Reprovada', value: data.effectiveness.rejected, emphasis: 'danger' },
    {
      key: 'pending',
      label: 'Aguardando verificação',
      value: data.effectiveness.pending,
      emphasis: 'warning',
    },
  ];
}

// ─────────────────────────────────────────────────────────────────────────────
// Formatação
// ─────────────────────────────────────────────────────────────────────────────

/*
 * O CONSTRUTOR DE CSV NO CLIENTE FOI REMOVIDO.
 *
 * Ele existia porque não havia rota de exportação publicada no gateway, e um
 * botão que sempre devolve 404 é pior do que nenhum botão. Com
 * `GET /sgq/indicators/export` no ar, mantê-lo criaria o defeito que a auditoria
 * mais cobra: duas implementações do mesmo relatório, livres para divergir — foi
 * assim que o painel e o PDF do plano de ação passaram a calcular a situação da
 * tarefa de dois jeitos diferentes no legado (BUG-TAR-05). O arquivo agora é um
 * só, montado pelo servidor, e a exportação deixa evento na trilha.
 */

function format1(value: number): string {
  return value.toFixed(1).replace('.', ',');
}

function formatIsoAsBr(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return iso;
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
}
